
import {
  defineComponent,
  PropType,
  computed,
  ref,
  nextTick,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { DropdownOption } from '@/types';
import { groupVariables, EnrichedVariable } from '@/components/template-builder/utils/variables-list';
import VariableFormatPanel from '@/components/fields/partials/VariableFormatPanel.vue';
import { isFormatPanelAvailable } from '@/components/fields/partials/variable-format';

export default defineComponent({
  name: 'VariablesButton',

  components: {
    Dialog,
    InputText,
    Button,
    VariableFormatPanel,
  },

  props: {
    variablesList: {
      type: Array as PropType<DropdownOption[]>,
      required: true,
    },

    placeholder: {
      type: String,
      required: false,
      default: 'Variables',
    },
  },

  emits: {
    'on-choose-variable': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const isModalVisible = ref(false);
    const searchQuery = ref('');
    const openGroups = ref(new Set<string>());
    const searchInputRef = ref();
    const formatVariable = ref<EnrichedVariable | null>(null);

    const variables = computed<EnrichedVariable[]>(() => props.variablesList.map((variable: any) => ({
      value: variable.value,
      label: variable.label,
      displayLabel: (variable.isCustom || variable.isMetadata) ? variable.label : t(variable.label),
      isCustom: variable.isCustom || false,
      isFromCollection: variable.isFromCollection || false,
      isMetadata: variable.isMetadata || false,
      type: variable.type,
      preformatted: variable.preformatted,
    })));

    const groupedVariables = computed(() => groupVariables(variables.value, t));

    const filteredGroups = computed(() => {
      const query = searchQuery.value.toLowerCase().trim();
      if (!query) return groupedVariables.value;
      return groupedVariables.value
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => (item.displayLabel || t(item.label)).toLowerCase().includes(query)
              || item.value.toLowerCase().includes(query),
          ),
        }))
        .filter((group) => group.items.length > 0);
    });

    const openModal = () => {
      isModalVisible.value = true;
    };

    const onModalShow = () => {
      searchQuery.value = '';
      formatVariable.value = null;
      // Open first group by default
      openGroups.value = new Set(
        groupedVariables.value.length > 0 ? [groupedVariables.value[0].key] : [],
      );
      nextTick(() => {
        searchInputRef.value?.$el?.focus();
      });
    };

    const toggleGroup = (key: string) => {
      const next = new Set(openGroups.value);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      openGroups.value = next;
    };

    const selectVariable = (value: string | number) => {
      context.emit('on-choose-variable', value);
      isModalVisible.value = false;
    };

    // Format panel (step 2) — only on channels rendered by the v5 engine
    const formatPanelAvailable = computed(() => isFormatPanelAvailable());

    const openFormatPanel = (item: EnrichedVariable) => {
      formatVariable.value = item;
    };

    const onFormatInsert = (value: string) => {
      formatVariable.value = null;
      selectVariable(value);
    };

    const typeBadgeFor = (item: EnrichedVariable): string | null => {
      if (item.preformatted) {
        return t('templateBuilder.variableFormat.preformattedBadge');
      }
      if (item.type && ['number', 'decimal'].includes(item.type)) {
        return t('templateBuilder.variableFormat.typeNumber');
      }
      if (item.type && ['date', 'datetime'].includes(item.type)) {
        return t('templateBuilder.variableFormat.typeDate');
      }
      return null;
    };

    return {
      t,
      isModalVisible,
      searchQuery,
      openGroups,
      searchInputRef,
      formatVariable,
      formatPanelAvailable,
      groupedVariables,
      filteredGroups,
      openModal,
      onModalShow,
      toggleGroup,
      selectVariable,
      openFormatPanel,
      onFormatInsert,
      typeBadgeFor,
    };
  },
});
